/**
 * Pure helpers for deriving expansive-answer UI state.
 *
 * Kept out of the JSX so the spinner decision is unit-testable without
 * rendering the whole Response tree.
 */

/**
 * Decide whether the Expansive control should show a "Generating…"
 * spinner for a given chat entry.
 *
 * Unifies three cases so the live-stream view and the post-refresh /
 * cross-session view stay consistent:
 *   1. An explicit /generate_expansive click is in flight for this entry.
 *   2. Cross-session / post-refresh: the server is still producing the
 *      expansive answer for a conversation this tab is not streaming
 *      (detected via the sidebar status poll, before the result lands).
 *   3. Live auto-routed pipeline: this tab is streaming and has entered
 *      the expansive phase for the latest entry.
 *
 * Case 3 is intentionally NOT gated on ``hasPreliminary``. At the
 * concise→expansive boundary the concise text is committed to
 * ``preliminary_answer`` and the assistant buffer resets, so
 * ``hasPreliminary`` flips true immediately — yet research + synthesis
 * then run for seconds with only a placeholder visible. Gating case 3 on
 * ``hasPreliminary`` (as case 2 must, to stop once the persisted result
 * differs) hid the spinner for that whole window — the live-vs-refresh
 * inconsistency this unifies. Case 2 keeps the gate because after a
 * refresh a landed expansive result makes ``preliminary_answer`` and
 * the assistant text differ, which is the "done" signal there.
 *
 * @param {object} params
 * @param {boolean} params.loading - This tab has an in-flight ask stream.
 * @param {boolean} params.isLatestEntry - Entry is the most recent turn.
 * @param {boolean} params.isActiveConvServerRunning - Sidebar poll reports
 *   the conversation is still running server-side.
 * @param {boolean} params.hasPreliminary - A distinct preliminary_answer
 *   exists (concise committed, differs from the displayed assistant text).
 * @param {boolean} params.isExplicitExpansiveForEntry - An explicit
 *   expansive generation targets this entry (generatingExpansiveIndex
 *   matches entry.index).
 * @param {string} params.streamingPhase - Current stream phase
 *   ('preliminary' | 'full' | …).
 * @param {boolean} params.isExpansiveInProgress - Expansive-phase progress
 *   markers ("in-depth analysis", "calling agent") are present.
 * @returns {boolean} True when the "Generating…" spinner should show.
 */
export function shouldShowExpansiveSpinner({
  loading,
  isLatestEntry,
  isActiveConvServerRunning,
  hasPreliminary,
  isExplicitExpansiveForEntry,
  streamingPhase,
  isExpansiveInProgress,
}) {
  if (loading && isExplicitExpansiveForEntry) return true;
  if (isLatestEntry && isActiveConvServerRunning && !hasPreliminary) {
    return true;
  }
  if (
    loading &&
    isLatestEntry &&
    (streamingPhase === 'full' || isExpansiveInProgress)
  ) {
    return true;
  }
  return false;
}
